import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import PublicAccountSummary from "./PublicAccountSummary";
import {
  PUBLIC_SESSION_CONFIG,
  getPublicSession,
  getPublicSessionPayload,
  isPublicSessionError,
  logoutPublicSession,
} from "./publicAccountSession";
import { getPublicCreditHistory, getPublicCreditHistoryPayload } from "./publicDashboardApi";
import "./PublicDashboardEmbedPage.css";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SOURCE_LABELS = {
  subscription: "Subscription",
  promotional: "Promotional",
  paid: "Paid",
  mixed: "Promotional + Paid",
};

function responsePayload(response) {
  return response?.data?.data || response?.data || {};
}

function safeErrorMessage(error, fallback) {
  const payload = responsePayload(error?.response);
  const message = payload.message || payload.error;
  return typeof message === "string" && message.length <= 180 && !message.includes("_")
    ? message
    : fallback;
}

export function formatDashboardDate(value, includeTime = true) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-SG", includeTime
    ? { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }
    : { day: "numeric", month: "long", year: "numeric" }
  ).format(date);
}

function signedChange(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0";
  if (number > 0) return `+${number}`;
  if (number < 0) return `−${Math.abs(number)}`;
  return "0";
}

function ActivityRows({ transactions }) {
  return transactions.map((transaction, index) => {
    const change = Number(transaction.credit_delta) || 0;
    const key = transaction.id || `${transaction.occurred_at || "activity"}-${index}`;
    return (
      <tr key={key}>
        <td data-label="Date">{formatDashboardDate(transaction.occurred_at) || "—"}</td>
        <td data-label="Activity" className="public-dashboard-activity-title">{transaction.activity_title || "Credit activity"}</td>
        <td data-label="Credit Source">{SOURCE_LABELS[transaction.payment_source] || "Credits"}</td>
        <td data-label="Change" className={`public-dashboard-change ${change > 0 ? "is-positive" : change < 0 ? "is-negative" : ""}`}>
          {signedChange(transaction.credit_delta)}
        </td>
      </tr>
    );
  });
}

export default function PublicDashboardEmbedPage() {
  const [checkingSession, setCheckingSession] = useState(true);
  const [user, setUser] = useState(null);
  const [balances, setBalances] = useState(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [otpMessage, setOtpMessage] = useState("");
  const [accountActionError, setAccountActionError] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [historyCompleteFrom, setHistoryCompleteFrom] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const clearAccount = useCallback((message = "") => {
    setAccountActionError("");
    setUser(null);
    setBalances(null);
    setTransactions([]);
    setHistoryCompleteFrom(null);
    setHistoryError(false);
    setHistoryLoading(false);
    setOtpMessage(message);
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(false);
    try {
      const history = getPublicCreditHistoryPayload(await getPublicCreditHistory());
      if (!history.authenticated) {
        clearAccount("Your session has expired. Please verify your email again.");
        return false;
      }
      if (!history.success || history.error) {
        setHistoryError(true);
        return false;
      }
      setTransactions(history.transactions);
      setHistoryCompleteFrom(history.historyCompleteFrom);
      return true;
    } catch (error) {
      if (isPublicSessionError(error) || error?.response?.data?.authenticated === false) {
        clearAccount("Your session has expired. Please verify your email again.");
      } else {
        setHistoryError(true);
      }
      return false;
    } finally {
      setHistoryLoading(false);
    }
  }, [clearAccount]);

  const loadSession = useCallback(async ({ showLoading = false } = {}) => {
    if (showLoading) setCheckingSession(true);
    try {
      const session = getPublicSessionPayload(await getPublicSession());
      if (!session.authenticated || !session.user) {
        clearAccount();
        return null;
      }
      setUser(session.user);
      setBalances(session.balances);
      setAccountActionError("");
      setOtpMessage("");
      return session;
    } catch (error) {
      if (isPublicSessionError(error)) {
        clearAccount("Your session has expired. Please verify your email again.");
      } else {
        clearAccount("We could not check your account. Please try again.");
      }
      return null;
    } finally {
      if (showLoading) setCheckingSession(false);
    }
  }, [clearAccount]);

  useEffect(() => {
    let active = true;
    (async () => {
      const session = await loadSession();
      if (active) setCheckingSession(false);
      if (active && session) await loadHistory();
    })();
    return () => { active = false; };
  }, [loadHistory, loadSession]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const sendCode = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setOtpMessage("Please enter a valid email address.");
      return;
    }
    setSendingCode(true);
    setOtpMessage("Sending verification code…");
    try {
      await axios.post("/api/public/oral/send-email-code", { email: normalizedEmail });
      setResendCooldown(30);
      setOtpMessage(`Verification code sent to ${normalizedEmail}.`);
    } catch (error) {
      setOtpMessage(safeErrorMessage(error, "We could not send the verification code. Please try again."));
    } finally {
      setSendingCode(false);
    }
  };

  const verifyCode = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setOtpMessage("Please enter a valid email address.");
      return;
    }
    if (!/^\d{6}$/.test(code.trim())) {
      setOtpMessage("Please enter the six-digit verification code.");
      return;
    }
    setVerifyingCode(true);
    setOtpMessage("Verifying your email…");
    try {
      await axios.post("/api/public/oral/verify-email-code", {
        email: normalizedEmail,
        code: code.trim(),
        marketing_email_consent: false,
      }, PUBLIC_SESSION_CONFIG);
      const session = await loadSession();
      if (!session) {
        setOtpMessage("Email verified, but your account session could not be loaded. Please try again.");
        return;
      }
      setCode("");
      await loadHistory();
    } catch (error) {
      setOtpMessage(safeErrorMessage(error, "The verification code could not be verified. Please try again."));
    } finally {
      setVerifyingCode(false);
    }
  };

  const refresh = async () => {
    setAccountActionError("");
    setRefreshing(true);
    const session = await loadSession();
    if (session) await loadHistory();
    setRefreshing(false);
  };

  const logout = async () => {
    setAccountActionError("");
    setLoggingOut(true);
    try {
      await logoutPublicSession();
      clearAccount();
      setEmail("");
      setCode("");
    } catch (_) {
      setAccountActionError("Logout failed. Please try again.");
    } finally {
      setLoggingOut(false);
    }
  };

  if (checkingSession) return <main className="public-dashboard-page public-dashboard-loading">Checking your account…</main>;

  return (
    <main className="public-dashboard-page">
      <header className="public-dashboard-heading">
        <div>
          <h1>My Account</h1>
          <p>{user ? "View your AI credits and recent credit activity." : "Verify your email to view your AI credits and recent credit activity."}</p>
        </div>
        {user && <button type="button" className="public-dashboard-button secondary" onClick={refresh} disabled={refreshing || loggingOut}>{refreshing ? "Refreshing…" : "Refresh"}</button>}
      </header>

      {!user ? (
        <section className="public-dashboard-card public-dashboard-otp" aria-labelledby="verify-email-heading">
          <h2 id="verify-email-heading">Verify Your Email</h2>
          <div className="public-dashboard-fields">
            <label>Email Address<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={sendingCode || verifyingCode} autoComplete="email" /></label>
            <label>Six-digit Code<input type="text" inputMode="numeric" maxLength="6" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} disabled={verifyingCode} autoComplete="one-time-code" /></label>
          </div>
          <div className="public-dashboard-actions">
            <button type="button" className="public-dashboard-button secondary" onClick={sendCode} disabled={sendingCode || verifyingCode || resendCooldown > 0}>{sendingCode ? "Sending…" : resendCooldown > 0 ? `Send Again (${resendCooldown}s)` : "Send Code"}</button>
            <button type="button" className="public-dashboard-button primary" onClick={verifyCode} disabled={sendingCode || verifyingCode || !email.trim() || code.length !== 6}>{verifyingCode ? "Verifying…" : "Verify"}</button>
          </div>
          {otpMessage && <p className="public-dashboard-status" role="status">{otpMessage}</p>}
        </section>
      ) : (
        <>
          <PublicAccountSummary user={user} balances={balances} onLogout={logout} loggingOut={loggingOut} showAccountLink={false} />
          {accountActionError && <p className="public-dashboard-action-error" role="alert">{accountActionError}</p>}
          <section className="public-dashboard-card public-dashboard-history" aria-labelledby="credit-activity-heading">
            <div className="public-dashboard-section-heading">
              <h2 id="credit-activity-heading">Credit Activity</h2>
              {historyError && <button type="button" className="public-dashboard-button secondary small" onClick={loadHistory} disabled={historyLoading}>Retry</button>}
            </div>
            {historyLoading ? <p role="status">Loading credit activity…</p> : historyError ? <p role="alert">Credit activity is temporarily unavailable.</p> : transactions.length === 0 ? <p>No credit activity is available yet.</p> : (
              <div className="public-dashboard-table-wrap">
                <table><thead><tr><th>Date</th><th>Activity</th><th>Credit Source</th><th>Change</th></tr></thead><tbody><ActivityRows transactions={transactions} /></tbody></table>
              </div>
            )}
            {!historyLoading && !historyError && historyCompleteFrom && <p className="public-dashboard-history-note">Credit activity is available from {formatDashboardDate(historyCompleteFrom, false)}.</p>}
          </section>
        </>
      )}
    </main>
  );
}
